/**
 * 업무 프로젝트(PMS) — 프로젝트 목록·만들기(/tasks)와 프로젝트 안 업무 목록·추가(/tasks/:projectId).
 *
 * 한 컴포넌트가 세 화면(목록·업무·설정)을 맡는다(라우트 셋이 같은 인스턴스를 재사용한다) → projectId 가
 * 바뀌면 렌더 중에 상태를 초기화한다(effect 로 미루면 옛 필터로 한 번 더 요청이 나간다).
 * 업무 제목을 누르면 상세 서랍이 열리고 주소에 `?task={id}` 가 붙는다. 설정은 /tasks/:projectId/settings 페이지다.
 * 업무 추가는 머리 줄 «새 업무» 버튼이나 칸반 열의 «업무 추가»가 여는 가운데 창에서 한다(표 위 인라인 줄은 없다).
 * 댓글·첨부·기록은 업무 상세 서랍(taskpm/TaskPmTaskDetail)이 붙인다.
 * 노트는 보기 탭 줄 오른쪽 «노트» 가 여는 오른쪽 패널(taskpm/TaskPmNotesPanel)이 맡는다(`?notes=1`·`?note={id}`).
 * 왼쪽 메뉴는 게이트웨이 사이드바가 아니라 **안쪽 사이드바**(taskpm/TaskPmNav · 폭 240)다(L7).
 * 보기는 대시보드·칸반·리스트·간트·캘린더 다섯이고, 프로젝트를 열 때마다 «대시보드»(taskpm/TaskPmDashboard)로 시작한다(J4c2b).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useMatch, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { IconSettings, IconX } from '@tabler/icons-react';
import AppShell from '../components/AppShell';
import TaskPmCalendar from './taskpm/TaskPmCalendar';
import TaskPmDashboard from './taskpm/TaskPmDashboard';
import TaskPmGantt from './taskpm/TaskPmGantt';
import TaskPmKanban from './taskpm/TaskPmKanban';
import TaskPmNav from './taskpm/TaskPmNav';
import TaskPmNotesPanel from './taskpm/TaskPmNotesPanel';
import TaskPmProjectSettings from './taskpm/TaskPmProjectSettings';
import TaskPmTaskDetail from './taskpm/TaskPmTaskDetail';
import { errorText } from './taskpm/errorText';
import AssigneeTags from './supportboard/AssigneeTags';
import { taskPmAPI } from '../api/client';
import { alertDialog, toast } from '../components/AppDialog';
import useIsMobile from '../hooks/useIsMobile';
import { kstDateKey } from '../utils/msgDate';
import { COLORS, RADIUS, SHADOW, badge, btn, card, input } from '../utils/uiStyles';
import { IconButton } from '../components/ui';

const KEY_PATTERN = /^[A-Z][A-Z0-9]{1,9}$/;
const KEY_HINT = '영문 대문자로 시작하는 영숫자 2~10자';
const DEFAULT_COLOR = '#4C7DD9';
const NOT_FOUND = '프로젝트를 찾을 수 없거나 볼 권한이 없습니다.';
const SEARCH_DEBOUNCE_MS = 300;

const QUICK_FILTERS = [
  { value: '', label: '전체' },
  { value: 'open', label: '진행 중' },
  { value: 'done', label: '완료' },
  { value: 'overdue', label: '기한 지남' },
  { value: 'mine', label: '내 업무' },
];
const SORTS = [
  { value: 'number', label: '번호' },
  { value: 'due_date', label: '기한' },
  { value: 'updated_at', label: '최근 수정' },
];
// 보기 탭(잔디 순서). 프로젝트를 열 때마다 «대시보드» 로 시작한다(잔디처럼 기억하지 않는다 — 마스터 10-06 «기본보기 대시보드로»).
const VIEWS = [
  { value: 'dashboard', label: '대시보드' },
  { value: 'kanban', label: '칸반' },
  { value: 'list', label: '리스트' },
  { value: 'gantt', label: '간트' },
  { value: 'calendar', label: '캘린더' },
];

// 🔴 boxSizing: border-box 라야 th 의 width 가 padding 을 포함한 '실제 열 폭'이 된다(tableLayout: fixed 와 짝).
const th = { boxSizing: 'border-box', height: '33px', padding: '0 12px', textAlign: 'left', fontSize: '12px', fontWeight: 400, color: COLORS.textMuted, whiteSpace: 'nowrap', borderBottom: `1px solid ${COLORS.borderSoft}`, background: COLORS.surface };
const td = { boxSizing: 'border-box', height: '41px', padding: '0 12px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'middle', background: COLORS.surface, overflow: 'hidden' };
// 업무 표 열 폭(합 1337) — 왼쪽 «업무» 열만 고정하고 나머지는 가로로 스크롤한다.
const COL = { task: '393px', type: '128px', priority: '128px', status: '128px', start: '96px', due: '96px', assignee: '160px', creator: '128px', created: '80px' };
const TABLE_MIN_WIDTH = '1337px';

// 날짜 표시('2026-10-01' → '2026.10.01'). 값이 없으면 줄표.
const fmtDay = (d) => (d ? String(d).replaceAll('-', '.') : '—');

// 담당자 칸 — 2명까지 이름 + «외 N명»(지원보드 summarizeAssigneeNames 와 같은 규칙) · title 은 전체 이름.
// 🔴 `assignee_names` 가 없는 응답(옛 서버)은 `assignee_name` 그대로 · 이름이 없는(지워진) 사람은 서버의 이은 이름처럼 뺀다.
// 🔴 칸반(taskpm/TaskPmKanban)에 같은 함수가 있지만 import 하지 않는다 — r6 시험 넷이 그 파일을 default 만 있는
//    모의로 바꾼 채 리스트를 그린다. 규칙을 바꾸면 둘 다 바꾼다.
function assigneeSummary(task, max = 2) {
  const names = Array.isArray(task?.assignee_names) ? task.assignee_names.filter(Boolean) : [];
  if (names.length === 0) {
    const single = task?.assignee_name || '';
    return { text: single, title: single };
  }
  const overflow = names.length - max;
  const shown = names.slice(0, max).join(', ');
  return { text: overflow > 0 ? `${shown} 외 ${overflow}명` : shown, title: names.join(', ') };
}

// 🔴 늘 문자열을 받는다 — 422 의 detail 은 배열이라 그대로 그리면 React #31 로 화면이 통째로 깨진다.
const requestError = (e, fallback = '요청에 실패했습니다.') => errorText(e, fallback);
const statusOf = (e) => e?.response?.status;

// 🔴 오늘은 브라우저 '로컬' 날짜로 만든다 — toISOString() 은 KST 0~9시에 하루 어긋난다.
const pad2 = (n) => String(n).padStart(2, '0');
function localToday() {
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

const emptyForm = () => ({
  name: '', key: '', color: DEFAULT_COLOR, visibility: 'private', description: '',
});
// 새 업무 창의 폼. 선택지(상태·유형·우선순위)는 id 를 문자열로 들고 있다('' = 없음).
// 담당자는 여럿이다 — 멤버 user_id 목록(고른 차례 · [] = 없음).
const emptyTaskForm = (statusId) => ({
  title: '', assignees: [], start: '', due: '',
  statusId: statusId == null ? '' : String(statusId), typeId: '', priorityId: '',
});

export default function TaskPmPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // 설정도 주소가 곧 '열림' 상태다 — /tasks/:projectId/settings 는 업무 표 대신 설정 페이지를 그린다.
  const settingsMatch = useMatch('/tasks/:projectId/settings');
  const onSettings = Boolean(projectId && settingsMatch);
  // 업무 상세는 주소(?task=)가 곧 '열림' 상태다 — 링크로 바로 열리고 뒤로 가기로 닫힌다.
  const [searchParams, setSearchParams] = useSearchParams();
  const taskParam = searchParams.get('task');
  // 🔴 숫자만 받는다 — ?task=abc 는 무시한다(서버에 없는 id 를 묻지 않는다).
  const openTaskId = taskParam && /^\d+$/.test(taskParam) ? taskParam : null;
  // 노트 패널도 주소가 곧 '열림' 상태다(`?notes=1` = 목록 · `?note={id}` = 그 노트).
  const notesOpen = searchParams.get('notes') === '1';
  const noteParam = searchParams.get('note');
  const openNoteId = noteParam && /^\d+$/.test(noteParam) ? noteParam : null;

  // ── 목록 화면
  const [projects, setProjects] = useState([]);
  const [listError, setListError] = useState('');
  const [listBusy, setListBusy] = useState(false);
  const [tab, setTab] = useState('mine');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // ── 업무 화면
  const [project, setProject] = useState(null);
  const [projectError, setProjectError] = useState('');
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [taskError, setTaskError] = useState('');
  const [quick, setQuick] = useState('');
  const [search, setSearch] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [sort, setSort] = useState('number');
  const [view, setView] = useState('dashboard');
  const [taskForm, setTaskForm] = useState(() => emptyTaskForm(''));
  const [adding, setAdding] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);   // 새 업무 창
  const [filterOpen, setFilterOpen] = useState(false);   // 조건 줄은 잔디처럼 «필터링» 을 눌러야 펼쳐진다
  const [navKey, setNavKey] = useState(0);      // 사이드바 프로젝트 목록을 다시 읽게 하는 신호
  const mobile = useIsMobile();

  // 🔴 projectId 가 바뀌면 옛 프로젝트의 필터·목록이 새 프로젝트에 묻어 가지 않게 렌더 중에 되돌린다.
  const [shownFor, setShownFor] = useState(projectId);
  if (shownFor !== projectId) {
    setShownFor(projectId);
    setProject(null);
    setProjectError('');
    setTasks([]);
    setTotal(0);
    setTaskError('');
    setQuick('');
    setSearch('');
    setAppliedQ('');
    setSort('number');
    setTaskForm(emptyTaskForm(''));
    setTaskDialogOpen(false);   // 새 업무 창도 닫는다(옛 프로젝트의 선택지가 남은 폼이다)
    setFilterOpen(false);       // 조건도 되돌아가니 펼친 조건 줄도 접는다
    setView('dashboard');       // 다른 프로젝트도 «대시보드» 로 연다(보기는 기억하지 않는다)
  }

  // 늦게 온 응답이 최신 목록을 덮지 않게 요청마다 번호를 매긴다.
  const taskSeqRef = useRef(0);
  // 칸반에서 상태를 옮기는 중인 업무 id — 여러 장을 연달아 옮길 때 재조회를 마지막 한 번으로 모은다.
  const movingRef = useRef(new Set());

  // ── 프로젝트 목록 불러오기(/tasks 일 때만)
  useEffect(() => {
    if (projectId) return undefined;
    let alive = true;
    setListBusy(true);
    setListError('');
    taskPmAPI.listProjects()
      .then((res) => { if (alive) setProjects(Array.isArray(res?.data) ? res.data : []); })
      .catch((e) => { if (alive) setListError(requestError(e, '프로젝트 목록을 불러오지 못했습니다.')); })
      .finally(() => { if (alive) setListBusy(false); });
    return () => { alive = false; };
  }, [projectId]);

  // ── 프로젝트 상세
  useEffect(() => {
    if (!projectId) return undefined;
    let alive = true;
    taskPmAPI.getProject(projectId)
      .then((res) => {
        if (!alive) return;
        const data = res?.data || null;
        setProject(data);
        const firstStatus = data?.options?.status?.[0];
        setTaskForm(emptyTaskForm(firstStatus ? firstStatus.id : ''));
      })
      .catch((e) => {
        if (!alive) return;
        const code = statusOf(e);
        setProject(null);
        // 🔴 422 도 «없는 프로젝트» 로 본다 — /tasks/abc 처럼 숫자가 아닌 주소가 그 경로로 오는데,
        //    서버엔 검증 오류 처리기가 없어 pydantic 영어 문구가 그대로 보인다.
        setProjectError(code === 404 || code === 403 || code === 422 ? NOT_FOUND : requestError(e, NOT_FOUND));
      });
    return () => { alive = false; };
  }, [projectId]);

  // ── 업무 목록
  const loadTasks = useCallback(() => {
    if (!projectId) return;
    const seq = taskSeqRef.current + 1;
    taskSeqRef.current = seq;
    const params = { sort };
    if (quick) params.quick = quick;
    if (appliedQ.trim()) params.q = appliedQ.trim();
    taskPmAPI.listTasks(projectId, params)
      .then((res) => {
        if (taskSeqRef.current !== seq) return;          // 지나간 요청의 응답은 버린다
        setTasks(Array.isArray(res?.data?.items) ? res.data.items : []);
        setTotal(Number(res?.data?.total || 0));
        setTaskError('');
      })
      .catch((e) => {
        if (taskSeqRef.current !== seq) return;
        setTasks([]);
        setTotal(0);
        setTaskError(requestError(e, '업무 목록을 불러오지 못했습니다.'));
      });
  }, [projectId, quick, appliedQ, sort]);

  // 🔴 설정 주소(/tasks/:projectId/settings)에서도 업무 목록은 그대로 불러온다 — 이 의존성에는 화면
  //    구분 값이 없어서, 설정에서 막으면 «뒤로» 로 돌아왔을 때 다시 읽지 않아 표가 빈 채로 남는다.
  useEffect(() => { loadTasks(); }, [loadTasks]);

  // ── 설정 페이지가 프로젝트를 바꿨다(정보·멤버·선택지). 상세를 갈아 끼우고 업무도 다시 읽는다.
  const handleSettingsChanged = useCallback((detail) => {
    // 🔴 늦게 온 재조회가 다른 프로젝트 화면을 덮지 않게 한다.
    if (!detail || String(detail.id) !== String(projectId)) return;
    setProject(detail);
    setNavKey((k) => k + 1);                       // 이름·색이 바뀌었을 수 있다 → 사이드바 목록도 다시
    loadTasks();                                   // 상태·유형 이름이 바뀌었을 수 있다
    setTaskForm((prev) => {
      const statuses = detail.options?.status || [];
      if (statuses.some((s) => String(s.id) === String(prev.statusId))) return prev;
      return { ...prev, statusId: statuses[0] ? String(statuses[0].id) : '' };
    });
  }, [projectId, loadTasks]);

  // ── 업무 상세 열기·닫기. 설정은 별도 주소라 상세와 겹치지 않는다.
  const openTask = (id) => {
    setSearchParams({ task: String(id) });
  };
  const closeTask = () => setSearchParams({});

  // ── 노트 패널 열기·닫기. 🔴 setSearchParams 가 주소를 통째로 바꾸므로 노트 패널과 업무 서랍은
  //    동시에 열리지 않고, 둘 다 실린 주소(`?task=…&note=…`)면 업무가 이긴다.
  const openNotes = () => setSearchParams({ notes: '1' });
  const openNote = (id) => setSearchParams({ note: String(id) });
  const closeNotes = () => setSearchParams({});
  const notesShown = (notesOpen || Boolean(openNoteId)) && !openTaskId;

  // ── 보기(대시보드·칸반·리스트·간트·캘린더) 고르기 — 저장하지 않는다(다시 열면 «대시보드»).
  const chooseView = (v) => setView(v);

  // 검색은 타이핑이 멈춘 뒤에 보낸다(Enter 는 곧바로).
  useEffect(() => {
    if (search.trim() === appliedQ.trim()) return undefined;
    const timer = setTimeout(() => setAppliedQ(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, appliedQ]);

  // ── 목록 화면 데이터 가공
  const [joined, opened] = useMemo(() => {
    const sortRows = (rows) => [
      ...rows.filter((p) => !p.closed),
      ...rows.filter((p) => p.closed),
    ];
    return [
      sortRows(projects.filter((p) => p.is_member === true)),
      sortRows(projects.filter((p) => p.is_member !== true)),
    ];
  }, [projects]);
  const shown = tab === 'mine' ? joined : opened;

  const optionMap = useMemo(() => {
    const map = new Map();
    const groups = project?.options || {};
    ['status', 'type', 'priority'].forEach((kind) => {
      (groups[kind] || []).forEach((option) => map.set(option.id, option));
    });
    return map;
  }, [project]);

  // 하위 업무는 한 단계뿐이다(서버가 막는다). 부모가 이 목록에 있으면 그 바로 아래로 옮기고,
  // 없으면(검색·필터·200건 상한) 제자리에 둔다. 어떤 행도 빠지거나 두 번 나오면 안 된다.
  const taskRows = useMemo(() => {
    const ids = new Set(tasks.map((t) => t.id));
    const kids = new Map();
    tasks.forEach((t) => {
      if (t.parent_task_id && t.parent_task_id !== t.id && ids.has(t.parent_task_id)) {
        if (!kids.has(t.parent_task_id)) kids.set(t.parent_task_id, []);
        kids.get(t.parent_task_id).push(t);
      }
    });
    const out = [];
    const placed = new Set();
    tasks.forEach((t) => {
      if (placed.has(t.id)) return;
      if (t.parent_task_id && ids.has(t.parent_task_id) && t.parent_task_id !== t.id) return;  // 부모 아래에서 나온다
      out.push({ task: t, child: false });
      placed.add(t.id);
      (kids.get(t.id) || []).forEach((c) => {
        if (placed.has(c.id)) return;
        out.push({ task: c, child: true });
        placed.add(c.id);
      });
    });
    tasks.forEach((t) => { if (!placed.has(t.id)) { out.push({ task: t, child: false }); placed.add(t.id); } });  // 방어: 남은 것
    return out;
  }, [tasks]);

  const today = localToday();
  const keyValid = KEY_PATTERN.test(form.key);
  const canSaveProject = Boolean(form.name.trim()) && keyValid && !saving;
  const canAddTask = Boolean(project) && !project.closed && project.my_role !== 'viewer';

  // 🔴 현재 사용자는 렌더마다 읽는다(모듈 최상위 상수는 import 시점의 빈 값으로 굳는다 — TaskPmComments 와 같은 규칙).
  const myId = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}')?.id; } catch { return undefined; }
  })();
  // «나에게 할당» 은 내가 이 프로젝트의 멤버일 때만 — 멤버가 아니면 서버가 담당자 지정을 받지 않는다.
  const meMember = myId != null ? (project?.members || []).find((m) => String(m.user_id) === String(myId)) : undefined;
  const meIsMember = Boolean(meMember);
  // 담당자 태그의 후보 = 프로젝트 멤버(표시는 이름, 없으면 메일).
  const assigneePeople = (project?.members || []).map((m) => ({ id: m.user_id, name: m.name, email: m.email }));
  // 🔴 «나에게 할당» 은 바꾸지 않고 더한다(이미 있으면 그대로) — 멤버 행의 user_id 를 써서 태그 id 와 형이 같다.
  const assignMe = () => {
    if (!meMember) return;
    setTaskForm((prev) => (prev.assignees.includes(meMember.user_id)
      ? prev : { ...prev, assignees: [...prev.assignees, meMember.user_id] }));
  };

  // 새 업무 창 — 첫 상태로 비운 폼 / 기간 거꾸로 / 보낼 수 있는지
  const freshTaskForm = () => emptyTaskForm(project?.options?.status?.[0]?.id ?? '');
  const periodBad = Boolean(taskForm.start && taskForm.due && taskForm.start > taskForm.due);
  const canCreateTask = Boolean(taskForm.title.trim()) && !adding && !periodBad;
  // 접어도 조건은 그대로 걸려 있다 → 기본값과 다르면 «필터링» 버튼을 accent 로 칠해 걸림을 보인다.
  const filterActive = Boolean(quick) || Boolean(appliedQ.trim()) || sort !== 'number';

  const openDialog = () => {
    setForm(emptyForm());
    setFormError('');
    setDialogOpen(true);
  };

  // 안쪽 사이드바 «+» — 새 프로젝트 창은 목록 화면에만 있으니 프로젝트 안에서는 목록으로 옮겨 연다.
  const startNewProject = () => {
    if (projectId) navigate('/tasks', { state: { openNewProject: true } });
    else openDialog();
  };
  // 🔴 openDialog 를 의존성에 넣지 않는다 — 매 렌더 새 함수라 effect 가 매번 돈다.
  //    (CRA eslint 에는 exhaustive-deps 규칙이 없다 — disable 주석도 쓰지 않는다.)
  useEffect(() => {
    if (!projectId && location.state?.openNewProject) {
      openDialog();
      navigate('/tasks', { replace: true, state: null });   // 새로고침·뒤로 가기로 다시 열리지 않게
    }
  }, [projectId, location.state]);

  const saveProject = async () => {
    if (!canSaveProject) return;
    setSaving(true);
    setFormError('');
    try {
      const res = await taskPmAPI.createProject({
        name: form.name.trim(),
        key: form.key,
        color: form.color,
        visibility: form.visibility,
        description: form.description.trim(),
      });
      setDialogOpen(false);
      navigate(`/tasks/${res?.data?.id}`);
    } catch (e) {
      // 🔴 같은 키(409)·형식 오류(422)는 대화를 닫지 않는다 — 입력을 고쳐 다시 저장해야 한다.
      setFormError(requestError(e, '프로젝트를 만들지 못했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  // 창을 열 때마다 폼을 새로 비운다(옛 입력·지워진 선택지가 남지 않게).
  // statusId 를 주면 그 상태로 미리 골라 둔다(칸반 열의 «업무 추가»·«+») · 없으면 첫 상태다.
  const openTaskDialog = (statusId) => {
    if (openTaskId) closeTask();      // 상세 서랍과 겹쳐 열리지 않는다
    setTaskForm(statusId != null ? emptyTaskForm(statusId) : freshTaskForm());
    setTaskDialogOpen(true);
  };
  // 🔴 저장 중에는 닫지 않는다 — 응답을 기다리는 입력이 사라지면 실패해도 다시 쓸 수 없다.
  const closeTaskDialog = () => {
    if (adding) return;
    setTaskDialogOpen(false);
  };

  const addTask = async () => {
    // 제목·저장 중·기간 검사를 한곳에서 본다(Enter·«생성» 둘 다 여기로 온다).
    if (!canCreateTask) return;
    // 🔴 빈 마감일·빈 담당자 목록·고르지 않은 선택지는 키 자체를 뺀다 — '' 를 보내면 서버가 422 를 낸다.
    const body = { title: taskForm.title.trim() };
    if (taskForm.statusId) body.status_id = Number(taskForm.statusId);
    if (taskForm.typeId) body.type_id = Number(taskForm.typeId);
    if (taskForm.priorityId) body.priority_id = Number(taskForm.priorityId);
    if (taskForm.assignees.length > 0) body.assignee_user_ids = [...taskForm.assignees];
    if (taskForm.start) body.start_date = taskForm.start;
    if (taskForm.due) body.due_date = taskForm.due;
    setAdding(true);
    try {
      await taskPmAPI.createTask(projectId, body);
      setTaskDialogOpen(false);
      setTaskForm(freshTaskForm());
      loadTasks();
    } catch (e) {
      // 🔴 창도 입력도 그대로 둔다 — 고쳐서 다시 보내야 한다.
      const detail = requestError(e, '업무를 추가하지 못했습니다.');
      if (statusOf(e)) alertDialog(detail); else toast(detail, 'error');
    } finally {
      setAdding(false);
    }
  };

  // ── 칸반에서 카드를 다른 상태 열에 놓았다 — 먼저 화면을 바꾸고(낙관) 서버로 확인한다.
  const moveTask = async (task, statusId) => {
    if (!canAddTask || movingRef.current.has(task.id) || String(task.status_id) === String(statusId)) return;
    const option = optionMap.get(statusId);
    movingRef.current.add(task.id);
    // 🔴 이미 나가 있는 목록 요청의 응답이 낙관 반영을 옛 상태로 덮지 않게 번호를 넘긴다.
    taskSeqRef.current += 1;
    setTasks((prev) => prev.map((t) => (t.id === task.id
      ? { ...t, status_id: statusId, is_done: option ? Boolean(option.is_done) : t.is_done } : t)));
    try {
      await taskPmAPI.updateTask(task.id, { status_id: statusId });
    } catch (e) {
      const detail = requestError(e, '상태를 바꾸지 못했습니다.');
      if (statusOf(e)) alertDialog(detail); else toast(detail, 'error');
    } finally {
      movingRef.current.delete(task.id);
      // 성공이면 서버 값(기한 지남·필터 결과)으로, 실패면 원래 자리로 되돌린다.
      // 🔴 다른 카드를 옮기는 중이면 그 요청이 끝날 때 다시 읽는다 — 먼저 끝난 쪽의 재조회가
      //    아직 반영 안 된 다른 카드를 원래 열로 잠깐 되돌려 보이지 않게.
      if (movingRef.current.size === 0) loadTasks();
    }
  };

  const renderOption = (id, tone) => {
    const option = optionMap.get(id);
    if (!option) return <span style={{ color: COLORS.textMuted }}>—</span>;
    return (
      <span style={{ ...badge(tone), background: `${option.color}22`, color: option.color }}>{option.label}</span>
    );
  };

  // 새 업무 창의 라벨 글자(12 · 보조색) — 값은 그 아래에 둔다.
  const fieldLabel = { display: 'block', fontSize: '12px', color: COLORS.textSub };

  /**
   * 새 업무 창의 선택지 한 줄(잔디처럼 드롭다운이 아니라 버튼 묶음).
   * - 선택지가 없으면 줄 자체를 그리지 않는다.
   * - toggleable=false(상태)는 늘 하나가 선택돼 있다(누른 것으로 바뀌기만 한다).
   */
  const optionRow = (label, kind, field, toggleable) => {
    const options = project?.options?.[kind] || [];
    if (options.length === 0) return null;
    return (
      <div key={kind}>
        <div style={{ ...fieldLabel, marginBottom: '4px' }}>{label}</div>
        <div role="group" aria-label={label} style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {options.map((o) => {
            const on = String(taskForm[field]) === String(o.id);
            return (
              <button key={o.id} type="button" aria-pressed={on}
                onClick={() => setTaskForm((prev) => ({ ...prev, [field]: on && toggleable ? '' : String(o.id) }))}
                style={on ? btn('primary', 'sm') : btn('secondary', 'sm')}>{o.label}</button>
            );
          })}
        </div>
      </div>
    );
  };

  // 본문 좌우 안쪽 여백 36 · 위 여백은 머리 줄(minHeight 87)이 맡는다.
  const pagePad = mobile ? '0 16px 20px' : '0 36px 28px';

  // 🔴 «업무» 열은 데스크톱에서만 왼쪽에 고정한다 — 모바일(375)에서는 393 짜리 고정 열이 화면을 다 덮는다.
  //    borderCollapse: separate 라 오른쪽 경계는 inset 그림자로 그린다.
  const stickyTaskCell = {
    position: mobile ? 'static' : 'sticky', left: 0, zIndex: 1,
    boxShadow: `inset -1px 0 0 ${COLORS.border}`,
  };

  // 데스크톱에서는 안쪽 사이드바의 프로젝트 목록이 '목록으로' 를 대신한다 → 오류 화면·모바일에서만 그린다.
  const backToList = (
    <button type="button" onClick={() => navigate('/tasks')} className="gw-btn gw-btn--quiet" style={{ ...btn('quiet', 'sm'), marginBottom: '10px' }}>← 프로젝트 목록</button>
  );

  // ───────────────────────────────────────────── 프로젝트 목록 화면
  const listView = (
    <div style={{ padding: pagePad }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minHeight: '87px' }}>
        <h1 className="gw-h1" style={{ margin: 0 }}>업무 프로젝트</h1>
        {listBusy && <span role="status" style={{ color: COLORS.textMuted, fontSize: '12px' }}>불러오는 중…</span>}
        <button type="button" onClick={openDialog} style={{ ...btn('primary', 'sm'), marginLeft: 'auto' }}>새 프로젝트</button>
      </div>
      <p className="gw-ph-sub" style={{ margin: '0 0 16px' }}>
        프로젝트를 만들어 업무를 등록하고, 담당자·마감일·상태로 진행 상황을 함께 봅니다.
      </p>

      {listError && <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, marginBottom: '12px' }}>{listError}</div>}

      <div role="tablist" aria-label="프로젝트 범위" style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
        {[{ value: 'mine', label: '참여 중' }, { value: 'public', label: '공개' }].map((t) => (
          <button key={t.value} type="button" role="tab" aria-selected={tab === t.value}
            onClick={() => setTab(t.value)}
            style={tab === t.value ? btn('primary', 'sm') : btn('secondary', 'sm')}>{t.label}</button>
        ))}
      </div>

      {shown.length === 0
        ? <p style={{ textAlign: 'center', padding: '36px 16px', margin: 0, fontSize: '12px', color: COLORS.textMuted }}>아직 프로젝트가 없습니다. 새 프로젝트를 만들어 보세요.</p>
        : (
          // 폭 전체를 쓰는 한 줄 카드가 세로로 쌓인다(설명 줄은 빼고 키·업무 수만) — 시안 F 작업 카드 문법(r7 · 1px 테두리 + 그림자).
          // 시안에 프로젝트 목록 화면이 없어 좌우 여백 14 · 이름 굵기 600 은 정한 값이다.
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {shown.map((p) => (
              <button key={p.id} type="button" onClick={() => navigate(`/tasks/${p.id}`)}
                style={{
                  ...card, width: '100%', boxSizing: 'border-box', padding: '11px 14px', borderRadius: '7px',
                  border: `1px solid var(--gw-line, ${COLORS.borderSoft})`,
                  boxShadow: `0 1px 1px var(--gw-line, ${COLORS.borderSoft})`,
                  display: 'flex', alignItems: 'center', gap: '12px',
                  textAlign: 'left', cursor: 'pointer', opacity: p.closed ? 0.55 : 1,
                }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: p.color || DEFAULT_COLOR, display: 'inline-block', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <strong style={{ fontSize: '14px', fontWeight: 600, color: COLORS.text }}>{p.name}</strong>
                    {p.closed && <span style={badge('warn')}>종료</span>}
                  </div>
                  {/* 업무 수는 서버가 줄 때만 보인다 — 없으면 키만(서버 쪽은 별도 소잡). */}
                  <div style={{ fontSize: '11px', color: COLORS.textSub, marginTop: '4px' }}>
                    {typeof p.task_count === 'number' ? `${p.key} · 업무 ${p.task_count}개` : p.key}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <span style={badge(p.visibility === 'public' ? 'success' : 'default')}>
                    {p.visibility === 'public' ? '공개' : '비공개'}
                  </span>
                  <span style={{ fontSize: '11px', color: COLORS.textMuted }}>멤버 {p.member_count ?? 0}명</span>
                </div>
              </button>
            ))}
          </div>
        )}

      {dialogOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--gw-overlay, #19191945)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
          <div role="dialog" aria-label="새 프로젝트" aria-modal="true" className="gw-dialog"
            style={{ ...card, width: '520px', maxWidth: '92vw', borderRadius: RADIUS.xl, padding: '25px', boxShadow: SHADOW.float }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '14px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 620 }}>새 프로젝트</h2>
              <IconButton icon={IconX} label="닫기" onClick={() => setDialogOpen(false)} style={{ marginLeft: 'auto' }} />
            </div>
            <label style={{ display: 'block', fontSize: '12px', color: COLORS.textSub, marginBottom: '10px' }}>
              이름
              <input aria-label="이름" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                style={{ ...input, marginTop: '4px' }} />
            </label>
            <label style={{ display: 'block', fontSize: '12px', color: COLORS.textSub, marginBottom: '4px' }}>
              키
              <input aria-label="키" value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value.toUpperCase() })}
                style={{ ...input, marginTop: '4px' }} />
            </label>
            <p style={{ margin: '0 0 10px', fontSize: '11px', color: form.key && !keyValid ? COLORS.dangerText : COLORS.textMuted }}>{KEY_HINT}</p>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <label style={{ fontSize: '12px', color: COLORS.textSub }}>
                색
                <input type="color" aria-label="색" value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  style={{ display: 'block', marginTop: '4px', width: '56px', height: '32px', border: `1px solid ${COLORS.inputBorder}`, borderRadius: '8px', background: COLORS.surface }} />
              </label>
              <label style={{ flex: 1, fontSize: '12px', color: COLORS.textSub }}>
                공개 범위
                <select aria-label="공개 범위" value={form.visibility}
                  onChange={(e) => setForm({ ...form, visibility: e.target.value })}
                  style={{ ...input, marginTop: '4px' }}>
                  <option value="private">비공개</option>
                  <option value="public">공개</option>
                </select>
              </label>
            </div>
            <label style={{ display: 'block', fontSize: '12px', color: COLORS.textSub, marginBottom: '10px' }}>
              설명
              <textarea aria-label="설명" value={form.description} rows={3}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                style={{ ...input, marginTop: '4px', resize: 'vertical' }} />
            </label>
            {formError && <div role="alert" style={{ ...card, padding: '8px 10px', background: COLORS.dangerSoft, color: COLORS.dangerText, marginBottom: '10px', fontSize: '12px' }}>{formError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button type="button" onClick={() => setDialogOpen(false)} style={btn('secondary', 'sm')}>취소</button>
              <button type="button" onClick={saveProject} disabled={!canSaveProject} style={{ ...btn('primary', 'sm'), opacity: canSaveProject ? 1 : 0.5 }}>저장</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ───────────────────────────────────────────── 업무 목록 화면
  const taskView = (
    <div style={{ padding: pagePad }}>
      {projectError
        ? (
          // 🔴 머리 줄이 없는 화면이라 위 여백을 여기서 준다(pagePad 의 위쪽은 0 이다).
          <div style={{ paddingTop: '28px' }}>
            {backToList}
            <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText }}>{projectError}</div>
          </div>
        )
        : (
          <>
            {mobile && backToList}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minHeight: '87px' }}>
              <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: project?.color || DEFAULT_COLOR, display: 'inline-block', flexShrink: 0 }} />
              <h1 className="gw-h1" style={{ margin: 0 }}>{project?.name || ''}</h1>
              <span style={{ fontSize: '12px', color: COLORS.textSub }}>{project?.key || ''}</span>
              {project?.closed && <span style={badge('warn')}>종료된 프로젝트</span>}
              {/* 잔디 머리 줄: «새 업무»(83×32) 다음에 설정 톱니(36×36). */}
              {canAddTask && (
                // 🔴 인자 없이 부른다 — onClick={openTaskDialog} 면 클릭 이벤트가 statusId 로 들어간다.
                <button type="button" onClick={() => openTaskDialog()}
                  style={{ ...btn('primary', 'sm'), minWidth: '83px', height: '32px', boxSizing: 'border-box', marginLeft: 'auto' }}>새 업무</button>
              )}
              {/* 🔴 설정은 별도 주소다 — 이력에 쌓아(push) 브라우저 뒤로 가기로 업무 화면에 돌아온다.
                  `?task=` 는 새 주소에 붙지 않으니 closeTask 는 부르지 않고, 새 업무 창만 닫고 간다
                  (그 창은 포커스를 가두지 않아 열린 채로 Tab 으로 이 톱니를 누를 수 있다). */}
              <button type="button" aria-label="설정" title="설정"
                onClick={() => { setTaskDialogOpen(false); navigate(`/tasks/${projectId}/settings`); }}
                // 🔴 boxSizing 이 없으면 btn 의 테두리 1px 이 더해져 38×38 로 그려진다.
                // 🔴 marginLeft: auto 가 둘이면 남는 폭을 나눠 두 버튼이 떨어진다 → 새 업무가 있을 때는 톱니에 주지 않는다.
                style={{ ...btn('secondary', 'sm'), width: '36px', height: '36px', boxSizing: 'border-box', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginLeft: canAddTask ? 0 : 'auto', flexShrink: 0 }}>
                <IconSettings size={18} stroke={1.8} aria-hidden="true" />
              </button>
            </div>

            {/* 보기 탭 줄(잔디 39) — «대시보드»·«칸반»·«리스트»·«간트»·«캘린더» 다섯(잔디 순서 · 처음은 «대시보드»).
                🔴 오른쪽 «노트» 는 보기 탭이 아니라 노트 패널 여닫기다(잔디와 같다) → tablist '밖'에 둔다
                   (tablist 의 자식은 tab 이어야 하고, 탭 줄은 가로 스크롤이라 좁으면 밀려 사라진다). */}
            <div style={{ display: 'flex', alignItems: 'center', borderBottom: `1px solid ${COLORS.border}` }}>
              <div role="tablist" aria-label="보기" style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '39px', overflowX: 'auto', flex: 1, minWidth: 0 }}>
                {VIEWS.map((v) => {
                  const on = view === v.value;
                  return (
                    <button key={v.value} type="button" role="tab" aria-selected={on}
                      onClick={() => chooseView(v.value)}
                      style={{ height: '39px', boxSizing: 'border-box', padding: '0 14px', border: 'none', borderBottom: `2px solid ${on ? COLORS.primary : 'transparent'}`, background: 'none', color: on ? COLORS.primaryDark : COLORS.textSub, fontSize: '13px', fontWeight: on ? 600 : 500, cursor: 'pointer', whiteSpace: 'nowrap' }}>{v.label}</button>
                  );
                })}
              </div>
              <button type="button" aria-pressed={notesShown} onClick={notesShown ? closeNotes : openNotes}
                style={{ ...btn('secondary', 'sm'), height: '29px', minWidth: '70px', boxSizing: 'border-box', marginLeft: '8px', flexShrink: 0 }}>노트</button>
            </div>

            {/* 도구줄(잔디 48) — 오른쪽 끝 «필터링» 이 아래 조건 줄을 펼친다·접는다.
                🔴 대시보드에는 없다(대시보드 숫자는 필터를 따르지 않는다 — 아래 조건 줄도 같다). */}
            {view !== 'dashboard' && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '48px' }}>
              <button type="button" aria-expanded={filterOpen} aria-controls="task-pm-filters"
                onClick={() => setFilterOpen((v) => !v)}
                style={{ ...btn(filterActive ? 'accent' : 'secondary', 'sm'), minWidth: '80px', height: '32px', boxSizing: 'border-box' }}>필터링</button>
            </div>
            )}

            {/* 상세는 열렸을 때만 마운트한다. key 를 주어 다른 업무로 옮기면 처음부터 다시 읽는다. */}
            {openTaskId && project && (
              <TaskPmTaskDetail
                key={openTaskId}
                taskId={Number(openTaskId)}
                project={project}
                onOpenTask={openTask}
                onChanged={loadTasks}
                onClose={closeTask}
                onDeleted={() => { closeTask(); loadTasks(); }}
              />
            )}

            {/* 노트 패널도 열렸을 때만 마운트한다. key 를 주어 다른 노트로 옮기면 처음부터 다시 읽는다. */}
            {notesShown && project && (
              <TaskPmNotesPanel
                key={openNoteId || 'list'}
                project={project}
                noteId={openNoteId}
                canWrite={canAddTask}
                onOpenNote={openNote}
                onBackToList={openNotes}
                onClose={closeNotes}
              />
            )}

            {/* ── 새 업무 창(시안 F 520) — 머리 «새 업무» 버튼이나 칸반 열의 «업무 추가»로 열린다. */}
            {taskDialogOpen && project && (
              // 🔴 Esc 는 겉 틀에서 받는다(안쪽 입력에서 올라온다) · 한글 조합 중 Enter·Esc 는 무시한다.
              <div onKeyDown={(e) => { if (e.key === 'Escape' && !e.defaultPrevented && !e.nativeEvent?.isComposing) closeTaskDialog(); }}
                style={{ position: 'fixed', inset: 0, background: 'var(--gw-overlay, #19191945)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
                {/* 🔴 gw-dialog 클래스는 달지 않는다 — ≤600 의 padding 21 이 머리·본문·바닥 세 칸 바깥에 여백을 더한다. */}
                <div role="dialog" aria-label="새 업무" aria-modal="true"
                  style={{ ...card, padding: 0, width: '520px', maxWidth: '94vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', borderRadius: RADIUS.xl, boxShadow: SHADOW.float }}>
                  {/* 머리 69 */}
                  <div style={{ display: 'flex', alignItems: 'center', minHeight: '69px', padding: '0 20px', borderBottom: `1px solid ${COLORS.border}` }}>
                    <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 620 }}>새 업무</h2>
                    <IconButton icon={IconX} label="닫기" onClick={closeTaskDialog} style={{ marginLeft: 'auto' }} />
                  </div>

                  {/* 본문 — 제목 → 담당자 → 기간 → 상태·유형·우선순위(참조자·설명은 만든 뒤 상세에서 바꾼다) */}
                  <div style={{ padding: '16px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <label style={fieldLabel}>
                      업무 제목
                      <input aria-label="새 업무 제목" placeholder="업무 제목" value={taskForm.title} autoFocus
                        onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent?.isComposing && canCreateTask) addTask(); }}
                        style={{ ...input, marginTop: '4px' }} />
                    </label>

                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                      {/* 담당자는 여러 명(태그 묶음 role=group «담당자»).
                          🔴 <label> 로 감싸지 않는다 — label 안이면 label.control 이 첫 칩의 «제외» 버튼이 되어
                             라벨 글자·칩을 누를 때 첫 담당자가 빠진다. */}
                      <div style={{ ...fieldLabel, flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', marginBottom: '4px' }}>담당자</span>
                        <AssigneeTags ids={taskForm.assignees} people={assigneePeople} groupLabel="담당자"
                          onChange={(ids) => setTaskForm((prev) => ({ ...prev, assignees: ids }))} />
                      </div>
                      {/* 내가 이 프로젝트 멤버일 때만 보인다(멤버가 아니면 담당자로 지정할 수 없다). */}
                      {meIsMember && (
                        <button type="button" onClick={assignMe}
                          style={{ ...btn('secondary', 'sm'), height: '38px', boxSizing: 'border-box' }}>나에게 할당</button>
                      )}
                    </div>

                    <div>
                      {/* 🔴 두 칸은 flex: 1 로 나눈다 — 고정폭이면 좁은 창(94vw)에서 넘친다. */}
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                        <label style={{ ...fieldLabel, flex: 1, minWidth: 0 }}>
                          시작일
                          <input type="date" aria-label="시작일" value={taskForm.start}
                            onChange={(e) => setTaskForm({ ...taskForm, start: e.target.value })}
                            style={{ ...input, marginTop: '4px' }} />
                        </label>
                        <label style={{ ...fieldLabel, flex: 1, minWidth: 0 }}>
                          마감일
                          <input type="date" aria-label="마감일" value={taskForm.due}
                            onChange={(e) => setTaskForm({ ...taskForm, due: e.target.value })}
                            style={{ ...input, marginTop: '4px' }} />
                        </label>
                      </div>
                      {periodBad && (
                        <p style={{ margin: '6px 0 0', fontSize: '12px', color: COLORS.dangerText }}>마감일이 시작일보다 빠릅니다.</p>
                      )}
                    </div>

                    {optionRow('상태', 'status', 'statusId', false)}
                    {optionRow('유형', 'type', 'typeId', true)}
                    {optionRow('우선순위', 'priority', 'priorityId', true)}
                  </div>

                  {/* 바닥 67 */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', minHeight: '67px', padding: '0 20px', borderTop: `1px solid ${COLORS.border}` }}>
                    <button type="button" onClick={closeTaskDialog} disabled={adding}
                      style={{ ...btn('secondary', 'sm'), height: '38px', boxSizing: 'border-box' }}>취소</button>
                    <button type="button" onClick={addTask} disabled={!canCreateTask}
                      style={{ ...btn('primary', 'sm'), height: '38px', boxSizing: 'border-box', opacity: canCreateTask ? 1 : 0.5 }}>생성</button>
                  </div>
                </div>
              </div>
            )}

            {view !== 'dashboard' && filterOpen && (
            <section id="task-pm-filters" aria-label="업무 조건" style={{ ...card, padding: '12px 14px', marginBottom: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              {QUICK_FILTERS.map((f) => (
                <button key={f.value || 'all'} type="button" aria-pressed={quick === f.value}
                  onClick={() => setQuick(f.value)}
                  style={quick === f.value ? btn('primary', 'sm') : btn('secondary', 'sm')}>{f.label}</button>
              ))}
              <input type="search" aria-label="업무 검색" placeholder="제목 검색" value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') setAppliedQ(e.currentTarget.value); }}
                style={{ ...input, width: '180px' }} />
              <label style={{ fontSize: '12px', color: COLORS.textSub }}>
                정렬
                <select aria-label="정렬" value={sort} onChange={(e) => setSort(e.target.value)}
                  style={{ ...input, width: '130px', marginLeft: '4px' }}>
                  {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </label>
            </section>
            )}

            {taskError && <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, marginBottom: '12px' }}>{taskError}</div>}

            {/* 오류 블록은 다섯 보기 공통이고, 가운데 본문만 대시보드↔표↔칸반↔간트↔달력으로 갈린다.
                🔴 달력·간트는 리스트의 200건 목록과 따로 '보이는 범위' 를 스스로 읽는다(자기 안내도 스스로 그린다).
                🔴 대시보드는 서버 집계(/summary)를 스스로 읽는다 — refreshKey={tasks} 라 목록이 다시 읽힐 때마다
                   (상세에서 상태를 바꾸고 닫으면) 숫자도 다시 읽는다. 그래서 대시보드에서도 목록 effect 는 돈다.
                   key={projectId} — 다른 프로젝트로 옮기면 옛 프로젝트 숫자를 비우고 처음부터 읽는다. */}
            {view === 'dashboard' ? (
              <TaskPmDashboard
                key={projectId}
                projectId={projectId}
                refreshKey={tasks}
                // 카드로 건 조건이 리스트에서 보이게 조건 줄을 펼친다.
                onQuickFilter={(v) => { setQuick(v); setFilterOpen(true); chooseView('list'); }}
                onOpenTask={openTask}
              />
            ) : view === 'list' ? (
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              {/* 🔴 tableLayout: fixed 라야 긴 제목이 «업무» 열을 넓히지 않고 그 안에서 말줄임이 된다. */}
              <table style={{ tableLayout: 'fixed', minWidth: TABLE_MIN_WIDTH, width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                <thead>
                  <tr>
                    <th style={{ ...th, ...stickyTaskCell, width: COL.task }}>업무</th>
                    <th style={{ ...th, width: COL.type }}>유형</th>
                    <th style={{ ...th, width: COL.priority }}>우선순위</th>
                    <th style={{ ...th, width: COL.status }}>상태</th>
                    <th style={{ ...th, width: COL.start }}>시작일</th>
                    <th style={{ ...th, width: COL.due }}>마감일</th>
                    <th style={{ ...th, width: COL.assignee }}>담당자</th>
                    <th style={{ ...th, width: COL.creator }}>생성자</th>
                    <th style={{ ...th, width: COL.created }}>생성일</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.length === 0 && (
                    // 🔴 textAlign: center 는 넣지 않는다 — 표가 minWidth 1337 로 가로 스크롤이라 좁은 화면에서 문구가 화면 밖으로 간다.
                    <tr><td style={{ ...td, height: 'auto', padding: '36px 16px', fontSize: '12px', color: COLORS.textMuted }} colSpan={9}>조건에 맞는 업무가 없습니다.</td></tr>
                  )}
                  {taskRows.map(({ task: t, child }) => {
                    // 🔴 지난 마감 판정은 원본 날짜('YYYY-MM-DD')로 한다 — 보여 주는 점 구분 글자로 비교하면 안 된다.
                    const late = !t.is_done && t.due_date && t.due_date < today;
                    const assignee = assigneeSummary(t);
                    return (
                      <tr key={t.id}>
                        {/* 🔴 td 자체에는 flex 를 걸지 않는다(table-cell 이 깨진다) → 안쪽 div 가 가로 줄을 맡는다. */}
                        <td style={{ ...td, ...stickyTaskCell }}>
                          {/* 하위 업무는 부모 바로 아래 줄에 들여써 보인다(td 가 아니라 안쪽 div 에 건다). */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, paddingLeft: child ? '22px' : 0 }}>
                            <span style={{ color: COLORS.textSub, whiteSpace: 'nowrap', flexShrink: 0 }}>{t.key}</span>
                            <button type="button" onClick={() => openTask(t.id)}
                              style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', fontSize: '13px', color: COLORS.primaryDark, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {t.title}
                            </button>
                            {t.checklist_total > 0 && (
                              <span title="체크리스트" style={{ fontSize: '11px', color: COLORS.textSub, flexShrink: 0 }}>
                                {`${t.checklist_done || 0}/${t.checklist_total}`}
                              </span>
                            )}
                            {t.subtask_count > 0 && (
                              <span title="하위 업무" style={{ fontSize: '11px', color: COLORS.textSub, flexShrink: 0 }}>
                                {`하위 ${t.subtask_count}`}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={td}>{renderOption(t.type_id, 'default')}</td>
                        <td style={td}>{renderOption(t.priority_id, 'warn')}</td>
                        <td style={td}>{renderOption(t.status_id, 'default')}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDay(t.start_date)}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          <span data-testid={`due-${t.id}`} style={{ color: late ? COLORS.dangerText : COLORS.text, fontWeight: late ? 700 : 400 }}>
                            {fmtDay(t.due_date)}
                          </span>
                        </td>
                        <td title={assignee.title || undefined}
                          style={{ ...td, whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{assignee.text || '—'}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{t.created_by_name || '—'}</td>
                        {/* 생성일은 UTC naive ISO → KST 날짜 키를 거쳐 점 구분으로 보여 준다. */}
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDay(kstDateKey(t.created_at))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            ) : view === 'calendar' ? (
              // 🔴 refreshKey={tasks} — 업무 추가·상세 수정 뒤 loadTasks 가 새 배열을 넣으면 달력도 다시 읽는다.
              <TaskPmCalendar
                projectId={projectId}
                quick={quick}
                q={appliedQ}
                refreshKey={tasks}
                optionMap={optionMap}
                onOpenTask={openTask}
              />
            ) : view === 'gantt' ? (
              // 🔴 간트도 달력과 같다 — refreshKey={tasks} 로 추가·수정 뒤 보이는 범위를 다시 읽는다.
              //    막대를 끌어 기간을 바꾸면(canEdit) onChanged 로 리스트 목록도 함께 새로 읽는다.
              <TaskPmGantt
                projectId={projectId}
                quick={quick}
                q={appliedQ}
                refreshKey={tasks}
                optionMap={optionMap}
                statuses={project?.options?.status || []}
                onOpenTask={openTask}
                canEdit={canAddTask}
                onChanged={loadTasks}
              />
            ) : (
              <TaskPmKanban
                statuses={project?.options?.status || []}
                tasks={tasks}
                optionMap={optionMap}
                canEdit={canAddTask}
                onOpenTask={openTask}
                onAddTask={(statusId) => openTaskDialog(statusId)}
                onMove={moveTask}
              />
            )}
            {(view === 'list' || view === 'kanban') && total > tasks.length && (
              <p style={{ margin: '8px 0 0', fontSize: '12px', color: COLORS.textSub }}>처음 {tasks.length}개만 표시합니다(전체 {total}개).</p>
            )}
          </>
        )}
    </div>
  );

  // ───────────────────────────────────────────── 프로젝트 설정 화면(/tasks/:projectId/settings)
  // 🔴 project 가 오기 전에는 설정 본문을 마운트하지 않는다 — 정보 입력값을 마운트 때 project 에서 복사한다.
  const settingsView = (
    <div style={{ padding: pagePad }}>
      {projectError
        ? (
          // 업무 화면의 오류 블록과 같은 모양(머리 줄이 없는 화면이라 위 여백을 여기서 준다).
          <div style={{ paddingTop: '28px' }}>
            {backToList}
            <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText }}>{projectError}</div>
          </div>
        )
        : project
          ? (
            <TaskPmProjectSettings
              project={project}
              onChanged={handleSettingsChanged}
              onDeleted={() => { setNavKey((k) => k + 1); navigate('/tasks'); }}
              // «뒤로» 는 replace — 설정 주소를 이력에서 바꿔 써서, 돌아온 뒤 브라우저 뒤로 가기가
              // 설정으로 되돌아가지 않는다.
              onClose={() => navigate(`/tasks/${projectId}`, { replace: true })}
            />
          )
          : <p style={{ paddingTop: '28px', fontSize: '13px', color: COLORS.textSub }}>불러오는 중…</p>}
    </div>
  );

  return (
    <AppShell app="taskpm">
      {/* 🔴 AppSidebar 에 children 을 넘기지 않는다 — 프로젝트 목록은 게이트웨이 사이드바가 아니라
          아래 안쪽 사이드바가 맡는다(마스터 10-06). 좁은 화면에서는 폭 240 이 본문을 다 먹으므로
          그리지 않는다(이동은 목록 화면·«← 프로젝트 목록» 으로 한다). */}
      {!mobile && (
        <TaskPmNav currentProjectId={projectId || null} refreshKey={navKey} onNewProject={startNewProject} />
      )}
      <main style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto', background: COLORS.bg }}>
        {projectId ? (onSettings ? settingsView : taskView) : listView}
      </main>
    </AppShell>
  );
}
